import { LockOutlined, UserOutlined } from '@ant-design/icons';
import {
  AuthActions,
  AuthSource,
  getNetworkErrorMessage,
} from '@framework/auth';
import {
  FormHandler,
  FormItem,
  SubmitButton,
  TextInput,
} from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { yup } from '@framework/validation';
import { message } from 'antd';
import clx from 'classnames';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { down, up } from 'styled-breakpoints';
import styled from 'styled-components';
import { getMicDatiDipendente } from '../../api/employee/dati-dipendente-controller/dati-dipendente-controller';
import { usePostLdapLogin } from '../../api/employee/login-ldap-controller/login-ldap-controller';

// import classNames from 'classnames';
const LoginSubmitButton = styled(SubmitButton)`
  width: 100%;
  &.is-front-login {
    font-size: 17.25;
    text-align: center;
    font-weight: 600;
    padding: 0.95em 1em 0.85em;
    min-height: 46px;
    ${up('sm')} {
      &.is-horizontal {
        padding: 7px 15px;
        min-height: 40px;
        margin-top: 8px;
        flex: 1 1 34%;
      }
    }
  }
`;
const FormContainer = styled.div`
  &.is-front-login {
    min-height: 100px;
    ${down('sm')} {
      min-height: unset;
    }
    ${down('md')} {
      min-height: 140px;
    }
    ${up('sm')} {
      &.is-horizontal {
        flex: 1 1 66%;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-start;
        gap: 12px;
        min-height: unset;
        ${down('md')} {
          min-height: unset;
        }
        .ant-form-item {
          width: 100%;
        }
      }
    }
  }
`;

const FormGroupContainer = styled.div`
  padding-top: 20px;
  padding-bottom: 50px;

  &.is-front-login {
    padding-top: 0;
    ${up('sm')} {
      &.is-horizontal {
        padding-top: 0;
        padding-bottom: 0;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-start;
        gap: 12px;
      }
    }
  }
`;

const SoapLoginSchema = yup
  .object({
    username: yup.string().required().label('Nome utente'),
    password: yup.string().required().label('Password'),
  })
  .required()
  .noUnknown();

type ISoapLoginData = yup.InferType<typeof SoapLoginSchema>;

export interface ISoapLoginFormProps {
  source: AuthSource;
  onLogin?: (response: any) => void;
  isFrontLogin?: boolean;
  isHorizontal?: boolean;
}

/**
 * Form di login per gli utenti LDAP/SOAP (utenti interni, dipendenti)
 */
export function SoapLoginForm(props: ISoapLoginFormProps) {
  const { onLogin, source, isFrontLogin, isHorizontal } = props;

  const dispatch = useDispatch();

  const { t } = useTranslation();

  const login = usePostLdapLogin({
    request: {
      headers: {
        'X-Login-Provenienza': source,
      },
    },
  });

  const handleSubmit = useCallback(
    async (values: ISoapLoginData) => {
      try {
        const result: any = await login.mutateAsync({
          data: values,
        });

        // Recupero i dati del personale dal servizio LDAP
        const data = await getMicDatiDipendente({
          headers: {
            Authorization: `Bearer ${result.accessToken}`,
          },
        });

        dispatch(AuthActions.userDataLoaded(data.data ?? {}));

        onLogin?.(result);
      } catch (e) {
        message.error(getNetworkErrorMessage(e, 'Si è verificato un errore durante il login.')); // prettier-ignore
        console.error(`[LoginForm] Errore durante il login`, { error: e });
      }
    },
    [dispatch, login, onLogin],
  );

  return (
    <FormHandler schema={SoapLoginSchema} onSubmit={handleSubmit}>
      <FormGroupContainer
        className={clx({
          'is-front-login': isFrontLogin,
          'is-horizontal': isHorizontal,
        })}
        // style={{ paddingTop: isFrontLogin ? 0 : '20px', paddingBottom: '50px' }}
      >
        <FormContainer
          className={clx({
            'is-front-login': isFrontLogin,
            'is-horizontal': isHorizontal,
          })}
        >
          <FormItem
            label={t('user.login.employee-username', 'Nome utente')}
            name="username"
          >
            <TextInput
              autoFocus={isFrontLogin}
              name="username"
              prefix={
                <UserOutlined aria-hidden="true" style={{ color: '#757575' }} />
              }
              placeholder={t('user.login.employee-username', 'Nome utente')}
              disabled={login.isLoading}
            />
          </FormItem>
          <FormItem
            name="password"
            label={t('user.login.employee-password', 'Password')}
          >
            <TextInput
              name="password"
              type="password"
              placeholder={t('user.login.employee-password', 'Password')}
              prefix={
                <LockOutlined aria-hidden="true" style={{ color: '#757575' }} />
              }
              disabled={login.isLoading}
            />
          </FormItem>
        </FormContainer>

        <LoginSubmitButton
          pristineMessage={null}
          icon={null}
          loading={login.isLoading}
          className={clx({
            'is-front-login': isFrontLogin,
            'is-horizontal': isHorizontal,
          })}
        >
          {t('user.login.login-button', 'Accedi')}
        </LoginSubmitButton>
      </FormGroupContainer>
    </FormHandler>
  );
}
